import axios, { AxiosResponse } from 'axios';
import { LoginRequest, RegisterRequest, AuthResponse, ApiResponse } from '../types';
import { storage, storageSync } from '../utils/storage';
import { Capacitor } from '@capacitor/core';
import { WidgetBridgePlugin } from 'capacitor-widget-bridge';

// API Configuration - Use environment variable or staging by default
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://cluubz-backend-staging-294249cc77d1.herokuapp.com';

// App Group ID for iOS widget data sharing
const WIDGET_APP_GROUP = 'group.com.cluubz.app';

class ApiService {
  private static instance: ApiService;
  private token: string | null = null;
  private invitationCache: Map<string, { data: any, timestamp: number }> = new Map();
  private cacheTimeoutMs = 5000; // 5 seconds cache
  private masterInvitationData: { data: any, timestamp: number, dateRange: { start: string, end: string } } | null = null;
  private masterInvitationPromise: Promise<any> | null = null;
  private childrenCache: { data: any, timestamp: number } | null = null;
  private childrenPromise: Promise<any> | null = null;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;
  private tokenInitialized: boolean = false;
  private tokenInitPromise: Promise<void> | null = null;

  private constructor() {
    // Try sync first for web, then async for native
    this.token = storageSync.getItem('authToken');
    // Also initialize async for native platforms
    this.tokenInitPromise = this.initializeToken();
  }

  private async initializeToken(): Promise<void> {
    if (!this.tokenInitialized) {
      const storedToken = await storage.getItem('authToken');
      if (storedToken && !this.token) {
        this.token = storedToken;
      }
      this.tokenInitialized = true;

      // CRITICAL: Sync token to App Group on every app startup
      // This ensures the iOS widget always has the current token
      if (this.token) {
        await this.saveAuthToWidget(this.token);
        console.log('✅ [Widget] Synced auth token to App Group on startup');
      }
    }
  }

  // Public method to wait for token initialization (important for native platforms)
  public async waitForTokenInit(): Promise<void> {
    if (this.tokenInitPromise) {
      await this.tokenInitPromise;
    }
  }

  // Check if user has a valid token (after initialization)
  public async hasValidToken(): Promise<boolean> {
    await this.waitForTokenInit();
    return !!this.token;
  }

  // Get the current token (after initialization)
  public async getToken(): Promise<string | null> {
    await this.waitForTokenInit();
    return this.token;
  }

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  public setToken(token: string): void {
    this.token = token;
  }

  private getAuthHeaders() {
    return this.token ? { Authorization: `Bearer ${this.token}` } : {};
  }

  // Get a master date range that covers all component needs
  private getMasterDateRange(): { start: string, end: string } {
    const today = new Date();
    // Start from beginning of current week to cover ChildrenScreen's current week view
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
    
    // End 1 year from now to cover NotificationBell and ChildActivityScreen's 1-year view
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(today.getFullYear() + 1);
    
    return {
      start: startOfWeek.toISOString().split('T')[0],
      end: oneYearLater.toISOString().split('T')[0]
    };
  }

  // Check if requested date range is covered by master data
  private isDateRangeCovered(requestedStart: string, requestedEnd: string, masterStart: string, masterEnd: string): boolean {
    return requestedStart >= masterStart && requestedEnd <= masterEnd;
  }

  // Filter master data to requested date range
  private filterInvitationsByDateRange(invitations: any[], startDate: string, endDate: string): any[] {
    if (!invitations) return [];
    
    return invitations.filter(invitation => {
      const activityStartDate = invitation.start_date?.split('T')[0] || '';
      const activityEndDate = invitation.end_date?.split('T')[0] || activityStartDate;
      
      // Activity overlaps with requested range if:
      // Activity start <= requested end AND activity end >= requested start
      return activityStartDate <= endDate && activityEndDate >= startDate;
    });
  }

  private async request<T>(
    method: 'get' | 'post' | 'put' | 'delete' | 'patch',
    endpoint: string,
    data?: any,
    options?: { retryCount?: number; timeout?: number }
  ): Promise<ApiResponse<T>> {
    const retryCount = options?.retryCount ?? 0;
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 1000; // Start with 1 second

    try {
      const config = {
        method,
        url: `${API_BASE_URL}${endpoint}`,
        headers: {
          'Content-Type': 'application/json',
          ...this.getAuthHeaders(),
        },
        data,
        timeout: options?.timeout ?? 30000,
      };

      const response: AxiosResponse<T> = await axios(config);

      // Handle different backend response formats
      const responseData = response.data as any;

      // For auth endpoints, the backend returns { success, token, user } directly
      if (endpoint.includes('/auth/') && responseData.success) {
        return {
          success: true,
          data: responseData,
        };
      }

      // For other endpoints, check if response.data has a nested 'data' property
      const actualData = responseData?.data !== undefined
        ? responseData.data
        : responseData;

      return {
        success: true,
        data: actualData,
      };
    } catch (error: any) {
      const status = error.response?.status;
      const isNetworkError = !error.response || error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
      const isServerError = status && status >= 500;
      const isRetryable = isNetworkError || isServerError;

      console.error(`API ${method.toUpperCase()} ${endpoint} error (attempt ${retryCount + 1}/${MAX_RETRIES + 1}):`, {
        message: error.message,
        status: status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        code: error.code,
        isRetryable,
        timestamp: new Date().toISOString()
      });

      // Retry logic for network errors and server errors (but not auth errors like 401/403)
      if (isRetryable && retryCount < MAX_RETRIES) {
        const delay = RETRY_DELAY_MS * Math.pow(2, retryCount); // Exponential backoff
        console.log(`⏳ Retrying request after ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        return this.request<T>(method, endpoint, data, { ...options, retryCount: retryCount + 1 });
      }

      // If it's a 401 (authentication error), try to refresh the token
      // Note: 403 (forbidden/authorization) means the user lacks permission - refreshing won't help
      if (status === 401) {
        // Don't try to refresh if this IS the refresh or login endpoint (prevent infinite loop)
        // Also limit auth refresh retries to prevent infinite loops
        if (!endpoint.includes('/auth/refresh') && !endpoint.includes('/auth/login') && retryCount < 1) {
          console.log('🔄 Got 401, attempting token refresh...');
          const refreshed = await this.attemptTokenRefresh();

          if (refreshed) {
            console.log('✅ Token refreshed, retrying original request...');
            // Retry the original request with the new token (increment retryCount to prevent infinite loop)
            return this.request<T>(method, endpoint, data, { ...options, retryCount: retryCount + 1 });
          }

          // Refresh failed, return session expired error
          return {
            success: false,
            error: 'Your session has expired. Please log in again.',
          };
        }

        // For login/refresh endpoints, return the actual server error
        return {
          success: false,
          error: error.response?.data?.error || error.message || 'Authentication failed',
        };
      }

      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Request failed',
      };
    }
  }

  // Attempt to refresh the token when getting 401/403
  private async attemptTokenRefresh(): Promise<boolean> {
    // If already refreshing, wait for that refresh to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    // Start refresh process
    this.isRefreshing = true;
    this.refreshPromise = this.performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      console.log('🔄 Calling refresh endpoint...');
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/refresh`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            ...this.getAuthHeaders(),
          },
        }
      );

      if (response.data?.success && response.data?.token) {
        console.log('✅ Token refresh successful');
        // Update token in service and storage
        this.token = response.data.token;
        await storage.setItem('authToken', response.data.token);
        // Update widget auth token
        await this.saveAuthToWidget(response.data.token);

        // Update user data if provided
        if (response.data.user) {
          await storage.setItem('userData', JSON.stringify(response.data.user));
        }

        return true;
      }

      console.log('❌ Token refresh failed: no token in response');
      return false;
    } catch (error) {
      console.log('❌ Token refresh failed:', error);
      return false;
    }
  }

  // Public HTTP methods
  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    return this.request<T>('post', endpoint, data);
  }

  // Save auth token and API URL to widget App Group for iOS widget API calls
  private async saveAuthToWidget(token: string): Promise<void> {
    try {
      // Only run on iOS native app
      if (Capacitor.getPlatform() !== 'ios') return;

      // Save auth token to App Group for widget to use
      await WidgetBridgePlugin.setItem({
        key: 'authToken',
        value: token,
        group: WIDGET_APP_GROUP
      });

      // Save API base URL so widget knows where to call
      await WidgetBridgePlugin.setItem({
        key: 'apiBaseUrl',
        value: API_BASE_URL,
        group: WIDGET_APP_GROUP
      });

      console.log('✅ [Widget] Saved auth token and API URL to App Group');
    } catch (error) {
      // Silently fail - widget auth is not critical
      console.log('ℹ️ [Widget] Could not save auth to App Group:', error);
    }
  }

  // Clear widget auth data on logout
  private async clearWidgetAuth(): Promise<void> {
    try {
      if (Capacitor.getPlatform() !== 'ios') return;

      await WidgetBridgePlugin.setItem({
        key: 'authToken',
        value: '',
        group: WIDGET_APP_GROUP
      });

      // Reload widget to show empty state
      await WidgetBridgePlugin.reloadAllTimelines();

      console.log('✅ [Widget] Cleared auth token from App Group');
    } catch (error) {
      console.log('ℹ️ [Widget] Could not clear widget auth:', error);
    }
  }

  // Refresh widget to fetch latest activities (public so it can be called on app resume)
  async refreshWidget(): Promise<void> {
    try {
      if (Capacitor.getPlatform() !== 'ios') return;

      // Always sync token before refreshing widget (ensures widget has current auth)
      if (this.token) {
        await this.saveAuthToWidget(this.token);
      }

      await WidgetBridgePlugin.reloadAllTimelines();
      console.log('✅ [Widget] Synced token and triggered widget refresh');
    } catch (error) {
      console.log('ℹ️ [Widget] Could not refresh widget:', error);
    }
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>('post', '/api/auth/login', credentials);
    if (response.success && response.data) {
      this.token = response.data.token;
      // Use async storage for native platforms (iOS/Android)
      await storage.setItem('authToken', response.data.token);
      await storage.setItem('userData', JSON.stringify(response.data.user));
      // Save token to widget App Group for widget API calls
      await this.saveAuthToWidget(response.data.token);
      // Refresh widget to load user's activities
      await this.refreshWidget();
    }
    return response;
  }

  async register(userData: RegisterRequest): Promise<ApiResponse<AuthResponse>> {
    const response = await this.request<AuthResponse>('post', '/api/auth/register', userData);
    // Don't automatically set token/localStorage - let AuthContext handle it
    return response;
  }

  async verifyToken(): Promise<ApiResponse<{ user: any }>> {
    return this.request('get', '/api/auth/verify');
  }

  async logout(): Promise<ApiResponse<any>> {
    try {
      // Call server logout endpoint to invalidate server-side session
      const response = await this.request('post', '/api/auth/logout');

      // Clear client-side data regardless of server response
      this.token = null;
      await storage.removeItem('authToken');
      await storage.removeItem('userData');
      // Clear widget auth data
      await this.clearWidgetAuth();

      return response;
    } catch (error) {
      // Still clear client-side data even if server call fails
      this.token = null;
      await storage.removeItem('authToken');
      await storage.removeItem('userData');
      // Clear widget auth data
      await this.clearWidgetAuth();

      console.error('Logout error:', error);
      return { success: false, error: 'Logout completed locally but server logout failed' };
    }
  }

  // Profile
  async getProfile(): Promise<ApiResponse<any>> {
    return await this.request('get', '/api/users/profile');
  }

  async updateProfile(profileData: { username?: string; email?: string; phone?: string; address_line_1?: string; town_city?: string; state_province_country?: string; post_code?: string; onboarding_completed?: boolean }): Promise<ApiResponse<any>> {
    const response = await this.request('put', '/api/users/profile', profileData);
    if (response.success && response.data) {
      // Update stored user data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const updatedUserData = { ...userData, ...response.data };
      localStorage.setItem('userData', JSON.stringify(updatedUserData));
    }
    return response;
  }

  async changePassword(passwordData: { currentPassword: string; newPassword: string }): Promise<ApiResponse<any>> {
    return this.request('put', '/api/users/change-password', passwordData);
  }

  async deleteAccount(password: string): Promise<ApiResponse<any>> {
    return this.request('delete', '/api/users/delete-account', { password });
  }

  async dismissSkeletonWelcome(): Promise<ApiResponse<any>> {
    return this.request('post', '/api/users/dismiss-skeleton-welcome');
  }

  async saveCookieConsent(preferences: {
    necessary: boolean;
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
  }): Promise<ApiResponse<any>> {
    // Generate a session ID if user is not logged in
    let sessionId = localStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('sessionId', sessionId);
    }

    return this.request('post', '/api/users/cookie-consent', {
      ...preferences,
      sessionId,
    });
  }

  // Push Notifications / Device Tokens
  async registerDeviceToken(token: string, platform: string): Promise<ApiResponse<any>> {
    return this.request('post', '/api/device-tokens/register', {
      token,
      platform,
      device_info: {
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    });
  }

  async unregisterDeviceToken(token: string): Promise<ApiResponse<any>> {
    return this.request('post', '/api/device-tokens/unregister', { token });
  }

  // Parents
  async getParents(): Promise<ApiResponse<any[]>> {
    // Add cache-busting parameter to force fresh request
    const timestamp = Date.now();
    return this.request('get', `/api/parents?_t=${timestamp}`);
  }

  async createParent(parentData: { username: string; email: string; phone: string; password: string; role?: string }): Promise<ApiResponse<any>> {
    return this.request('post', '/api/parents', parentData);
  }

  async deleteParent(parentUuid: string): Promise<ApiResponse<any>> {
    return this.request('delete', `/api/parents/${parentUuid}`);
  }

  async updateParent(parentUuid: string, parentData: any): Promise<ApiResponse<any>> {
    return this.request('put', `/api/parents/${parentUuid}`, parentData);
  }

  // Children - with promise deduplication and caching
  async getChildren(): Promise<ApiResponse<any[]>> {
    // Check if we have valid cached data
    if (this.childrenCache && (Date.now() - this.childrenCache.timestamp) < this.cacheTimeoutMs) {
      return { success: true, data: this.childrenCache.data };
    }

    // If there's already a request in flight, return the same promise
    if (this.childrenPromise) {
      try {
        const result = await this.childrenPromise;
        return result;
      } catch (error) {
        // If the existing promise failed, clear it and try again
        this.childrenPromise = null;
      }
    }

    // Create new promise for the API request
    this.childrenPromise = this.fetchChildren();

    try {
      const result = await this.childrenPromise;
      return result;
    } finally {
      // Clear the promise when done (success or failure)
      this.childrenPromise = null;
    }
  }

  // Separate method for the actual children API call
  private async fetchChildren(): Promise<ApiResponse<any[]>> {
    // Add cache-busting parameter to force fresh request
    const timestamp = Date.now();
    const response = await this.request<any[]>('get', `/api/children?_t=${timestamp}`);

    if (response.success && response.data) {
      // Cache successful responses
      this.childrenCache = {
        data: response.data,
        timestamp: Date.now()
      };
    }

    return response;
  }

  async createChild(childData: { first_name: string; last_name?: string; name?: string }): Promise<ApiResponse<any>> {
    const response = await this.request('post', '/api/children', childData);
    // Clear children cache when data changes
    if (response.success) {
      this.childrenCache = null;
      this.childrenPromise = null;
    }
    return response;
  }

  async deleteChild(childUuid: string): Promise<ApiResponse<any>> {
    const response = await this.request('delete', `/api/children/${childUuid}`);
    // Clear children cache when data changes
    if (response.success) {
      this.childrenCache = null;
      this.childrenPromise = null;
    }
    return response;
  }

  async updateChild(childUuid: string, childData: any): Promise<ApiResponse<any>> {
    const response = await this.request('put', `/api/children/${childUuid}`, childData);
    // Clear children cache when data changes
    if (response.success) {
      this.childrenCache = null;
      this.childrenPromise = null;
    }
    return response;
  }

  // Activities
  async getActivities(childUuid: string): Promise<ApiResponse<any[]>> {
    return this.request<any[]>('get', `/api/activities/${childUuid}`);
  }

  async getCalendarActivities(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    return this.request('get', `/api/calendar/activities?start=${startDate}&end=${endDate}&_t=${Date.now()}`);
  }

  async getActivity(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('get', `/api/activity/${activityUuid}`);
  }

  async createActivity(childUuid: string, activityData: any): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${childUuid}`, activityData);
  }

  async createParentActivity(parentUuid: string, activityData: any): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/parent/${parentUuid}`, activityData);
  }

  // Unified activity creation - handles both children and parents in one call
  async createActivitiesUnified(
    participantChildUuids: string[],
    participantParentUuids: string[],
    activityData: any
  ): Promise<ApiResponse<any>> {
    return this.request('post', '/api/activities', {
      ...activityData,
      participant_child_uuids: participantChildUuids,
      participant_parent_uuids: participantParentUuids,
    });
  }

  async createActivitiesBatch(childUuid: string, activities: any[]): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/batch/${childUuid}`, { activities });
  }

  async updateActivity(activityUuid: string, activityData: any): Promise<ApiResponse<any>> {
    return this.request('put', `/api/activities/update/${activityUuid}`, activityData);
  }

  async deleteActivity(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('delete', `/api/activities/delete/${activityUuid}`);
  }

  async dismissActivity(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('patch', `/api/activities/dismiss/${activityUuid}`);
  }

  // Configure activity kit items and/or designated parent (with optional series support)
  async configureActivity(
    activityUuid: string,
    config: {
      kit_needed?: string[];
      responsible_parent_uuid?: string | null;
      reminder_minutes?: number | null;
      reminder_target?: 'start' | 'end' | 'both' | null;
      apply_to_series?: boolean;
    }
  ): Promise<ApiResponse<any>> {
    return this.request('patch', `/api/activities/${activityUuid}/configure`, config);
  }

  async deleteActivitySeries(activityName: string, childUuid: string): Promise<ApiResponse<any>> {
    // Try to delete entire series by activity name and child UUID
    // This will delete all activities with the same name for the specific child
    return this.request('delete', `/api/activities/delete-series`, {
      activity_name: activityName,
      child_uuid: childUuid
    });
  }

  async duplicateActivity(activityUuid: string, newStartDate: string, newEndDate: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/duplicate`, {
      new_start_date: newStartDate,
      new_end_date: newEndDate
    });
  }

  async markActivityCantAttend(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/cant-attend`);
  }

  async markActivityCanAttend(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/can-attend`);
  }

  async getConnectedActivities(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    return this.request('get', `/api/calendar/connected-activities?start=${startDate}&end=${endDate}`);
  }

  // Master invitation data loader - loads once with wide date range to serve all components
  // Uses promise deduplication to prevent simultaneous requests
  private async loadMasterInvitationData(): Promise<{ calendar_invitations: any[], pending_invitations: any[], stats: any } | null> {
    const masterRange = this.getMasterDateRange();
    
    // Check if we have valid master data that covers the full range
    if (this.masterInvitationData &&
        (Date.now() - this.masterInvitationData.timestamp) < this.cacheTimeoutMs &&
        this.masterInvitationData.dateRange.start <= masterRange.start &&
        this.masterInvitationData.dateRange.end >= masterRange.end) {
      return this.masterInvitationData.data;
    }

    // If there's already a request in flight, return the same promise
    if (this.masterInvitationPromise) {
      try {
        const result = await this.masterInvitationPromise;
        return result;
      } catch (error) {
        // If the existing promise failed, clear it and try again
        this.masterInvitationPromise = null;
      }
    }

    // Create new promise for the API request
    this.masterInvitationPromise = this.fetchMasterInvitationData(masterRange);
    
    try {
      const result = await this.masterInvitationPromise;
      return result;
    } finally {
      // Clear the promise when done (success or failure)
      this.masterInvitationPromise = null;
    }
  }

  // Separate method for the actual API call
  private async fetchMasterInvitationData(masterRange: { start: string, end: string }): Promise<{ calendar_invitations: any[], pending_invitations: any[], stats: any } | null> {
    // Add cache buster to force fresh data from backend
    const cacheBuster = Date.now();
    const response = await this.request<{
      calendar_invitations: any[],
      pending_invitations: any[],
      stats: any
    }>('get', `/api/invitations/batch?start=${masterRange.start}&end=${masterRange.end}&_=${cacheBuster}`);
    
    if (response.success && response.data) {
      this.masterInvitationData = {
        data: response.data,
        timestamp: Date.now(),
        dateRange: masterRange
      };
      return response.data;
    }
    
    return null;
  }

  // Unified batch method to get all invitation data with intelligent caching
  async getBatchInvitations(startDate: string, endDate: string): Promise<ApiResponse<{
    calendar_invitations: any[],
    pending_invitations: any[],
    stats: any
  }>> {
    try {
      const masterData = await this.loadMasterInvitationData();
      
      if (!masterData) {
        return { success: false, error: 'Failed to load invitation data' };
      }
      
      // Filter master data to requested date range
      const filteredCalendarInvitations = this.filterInvitationsByDateRange(
        masterData.calendar_invitations, startDate, endDate
      );
      
      // Pending invitations don't need date filtering as they're status-based
      const filteredData = {
        calendar_invitations: filteredCalendarInvitations,
        pending_invitations: masterData.pending_invitations, // Always return all pending
        stats: {
          total_calendar: filteredCalendarInvitations.length,
          total_pending: masterData.pending_invitations?.length || 0,
          by_status: {} as Record<string, number>
        }
      };
      
      // Recalculate stats for filtered data
      filteredCalendarInvitations.forEach(invitation => {
        const status = invitation.status || 'unknown';
        filteredData.stats.by_status[status] = (filteredData.stats.by_status[status] || 0) + 1;
      });

      return { success: true, data: filteredData };
      
    } catch (error) {
      console.error('❌ Error in getBatchInvitations:', error);
      return { success: false, error: 'Failed to get batch invitations' };
    }
  }

  // Legacy method - kept for backward compatibility but now uses batch endpoint
  async getAllInvitations(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    const batchResponse = await this.getBatchInvitations(startDate, endDate);
    if (!batchResponse.success) {
      return batchResponse as any;
    }
    
    // Return just the calendar invitations for backward compatibility
    return {
      success: true,
      data: batchResponse.data?.calendar_invitations || []
    };
  }

  // Clear all caches (call when data changes)
  clearAllCaches(): void {
    this.invitationCache.clear();
    this.masterInvitationData = null;
    this.masterInvitationPromise = null;
    this.childrenCache = null;
    this.childrenPromise = null;
  }

  // Legacy method for backward compatibility
  clearInvitationCache(): void {
    this.clearAllCaches();
  }

  // Helper methods to filter invitations by status
  async getInvitedActivities(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    const batchResponse = await this.getBatchInvitations(startDate, endDate);
    if (!batchResponse.success) {
      return batchResponse as any;
    }

    // Filter accepted invitations from calendar invitations
    const acceptedInvitations = batchResponse.data?.calendar_invitations?.filter(invitation => invitation.status === 'accepted') || [];

    console.log('🟢 API getInvitedActivities - acceptedInvitations[0]:', acceptedInvitations[0]);
    console.log('🟢 activity_source in acceptedInvitations[0]:', acceptedInvitations[0]?.activity_source);

    // Debug specific activity
    const debugActivity = acceptedInvitations.find((a: any) => a.activity_uuid === 'ac2e074f-4b1a-405d-90e8-a4378132846b');
    if (debugActivity) {
      console.log('🔍 DEBUG ACTIVITY ac2e074f from backend:', {
        responsible_parent_uuid: debugActivity.responsible_parent_uuid,
        responsible_parent_first_name: debugActivity.responsible_parent_first_name,
        all_fields: Object.keys(debugActivity)
      });
    }

    // Normalize invitation UUIDs: uuid should be activity UUID, invitation_uuid should be invitation UUID
    const normalized = acceptedInvitations.map((inv: any) => ({
      ...inv,
      invitation_uuid: inv.invitation_uuid || inv.uuid,  // Preserve invitation UUID (calendar uses invitation_uuid, pending uses uuid)
      invitationUuid: inv.invitation_uuid || inv.uuid,   // CamelCase version of invitation UUID
      uuid: inv.activity_uuid || inv.uuid,  // Use activity UUID as primary identifier
      activity_uuid: inv.activity_uuid || inv.uuid  // Keep activity_uuid for reference
    }));

    console.log('🟢 API getInvitedActivities - normalized[0]:', normalized[0]);
    console.log('🟢 activity_source in normalized[0]:', normalized[0]?.activity_source);

    return {
      success: true,
      data: normalized
    };
  }

  async getDeclinedInvitations(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    const batchResponse = await this.getBatchInvitations(startDate, endDate);
    if (!batchResponse.success) {
      return batchResponse as any;
    }

    // Filter declined/rejected invitations from BOTH calendar and pending invitations
    // (Pitchero activities without child_id come from pending_invitations)
    const declinedFromCalendar = batchResponse.data?.calendar_invitations?.filter(
      invitation => invitation.status === 'declined' || invitation.status === 'rejected'
    ) || [];

    const declinedFromPending = batchResponse.data?.pending_invitations?.filter(
      invitation => invitation.status === 'declined' || invitation.status === 'rejected'
    ) || [];

    // Combine both arrays
    const declinedInvitations = [...declinedFromCalendar, ...declinedFromPending];

    // Normalize invitation UUIDs: uuid should be activity UUID, invitation_uuid should be invitation UUID
    const normalized = declinedInvitations.map((inv: any) => ({
      ...inv,
      invitation_uuid: inv.invitation_uuid || inv.uuid,  // Preserve invitation UUID (calendar uses invitation_uuid, pending uses uuid)
      invitationUuid: inv.invitation_uuid || inv.uuid,   // CamelCase version of invitation UUID
      uuid: inv.activity_uuid || inv.uuid,  // Use activity UUID as primary identifier
      activity_uuid: inv.activity_uuid || inv.uuid,  // Keep activity_uuid for reference
      isDeclinedInvitation: true  // Mark as declined for calendar rendering
    }));

    return {
      success: true,
      data: normalized
    };
  }

  async getActivityCounts(startDate: string, endDate: string, includeConnected: boolean = false): Promise<ApiResponse<any[]>> {
    return this.request('get', `/api/calendar/activity-counts?start=${startDate}&end=${endDate}&include_connected=${includeConnected}`);
  }

  // Connections
  async getConnectionRequests(): Promise<ApiResponse<any[]>> {
    // Add cache-busting parameter to force fresh request
    const timestamp = Date.now();
    return this.request('get', `/api/connections/requests?_t=${timestamp}`);
  }

  async getSentConnectionRequests(): Promise<ApiResponse<any[]>> {
    // Add cache-busting parameter to force fresh request
    const timestamp = Date.now();
    return this.request('get', `/api/connections/sent-requests?_t=${timestamp}`);
  }

  async searchParent(query: string, searchMethod: 'email' | 'phone' = 'email'): Promise<ApiResponse<any[]>> {
    return this.request('get', `/api/connections/search?q=${encodeURIComponent(query)}&method=${searchMethod}`);
  }

  async sendConnectionRequest(requestData: any): Promise<ApiResponse<any>> {
    return this.request('post', '/api/connections/request', requestData);
  }

  async createSkeletonAccount(requestData: any): Promise<ApiResponse<any>> {
    return this.request('post', '/api/connections/create-skeleton', requestData);
  }

  // ---- Class rep -----------------------------------------------------------

  async getClasses(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/classes');
  }

  async createClass(classData: {
    name: string;
    school_name?: string;
    year_group?: string;
    owner_child_uuid?: string;
  }): Promise<ApiResponse<any>> {
    return this.request('post', '/api/classes', classData);
  }

  async updateClass(classUuid: string, classData: any): Promise<ApiResponse<any>> {
    return this.request('put', `/api/classes/${classUuid}`, classData);
  }

  async deleteClass(classUuid: string): Promise<ApiResponse<any>> {
    return this.request('delete', `/api/classes/${classUuid}`);
  }

  // Either child_uuid (the parent was found by search and their child picked)
  // or child_name + parent_email (nobody matched, so hold a place for them).
  async addClassMember(classUuid: string, member: {
    child_uuid?: string;
    child_name?: string;
    child_first_name?: string;
    child_last_name?: string;
    parent_name?: string;
    parent_email?: string;
    message?: string;
  }): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/${classUuid}/members`, member);
  }

  async removeClassMember(classUuid: string, memberUuid: string): Promise<ApiResponse<any>> {
    return this.request('delete', `/api/classes/${classUuid}/members/${memberUuid}`);
  }

  // The classes my own children belong to, as opposed to the ones I run.
  async getClassMemberships(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/classes/memberships');
  }

  // Finding a class to join, and the rep answering that request.
  async searchClasses(school?: string, className?: string): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (school) params.set('school', school);
    if (className) params.set('class_name', className);
    return this.request('get', `/api/classes/search?${params.toString()}`);
  }

  async requestToJoinClass(classUuid: string, childUuid: string, message?: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/${classUuid}/join-request`, { child_uuid: childUuid, message });
  }

  async respondToJoinRequest(classUuid: string, memberUuid: string, action: 'accept' | 'decline'): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/${classUuid}/members/${memberUuid}/respond`, { action });
  }

  // Co-reps.
  async addClassRep(classUuid: string, rep: { parent_uuid?: string; child_uuid?: string; parent_email?: string }): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/${classUuid}/reps`, rep);
  }

  async removeClassRep(classUuid: string, repUuid: string): Promise<ApiResponse<any>> {
    return this.request('delete', `/api/classes/${classUuid}/reps/${repUuid}`);
  }

  async getClassRepInvitations(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/classes/rep-invitations');
  }

  async respondToClassRepInvitation(repUuid: string, action: 'accept' | 'decline', childUuid?: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/rep-invitations/${repUuid}/respond`, { action, child_uuid: childUuid });
  }

  // The invited parent's side of a class.
  async getClassInvitations(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/classes/invitations');
  }

  async respondToClassInvitation(memberUuid: string, action: 'accept' | 'decline'): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/invitations/${memberUuid}/respond`, { action });
  }

  async inviteClassToActivity(classUuid: string, activityUuid: string, message?: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/${classUuid}/invite`, { activity_uuid: activityUuid, message });
  }

  async remindClassAboutActivity(classUuid: string, activityUuid: string, message?: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/classes/${classUuid}/remind`, { activity_uuid: activityUuid, message });
  }

  async respondToConnectionRequest(requestUuid: string, action: 'accept' | 'reject'): Promise<ApiResponse<any>> {
    return this.request('post', `/api/connections/respond/${requestUuid}`, { action });
  }

  async getConnections(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/connections');
  }

  async getChildConnections(childUuid: string): Promise<ApiResponse<any[]>> {
    return this.request('get', `/api/connections/${childUuid}`);
  }

  async deleteConnection(connectionId: string): Promise<ApiResponse<any>> {
    // Try different possible endpoint patterns
    try {
      // First try the standard delete pattern
      return await this.request('delete', `/api/connections/${connectionId}`);
    } catch (error) {
      // If that fails, try using the respond endpoint to "remove" or "delete" the connection
      try {
        return await this.request('post', `/api/connections/respond/${connectionId}`, { action: 'delete' });
      } catch (error2) {
        // If that fails, try a different endpoint pattern
        try {
          return await this.request('post', `/api/connections/${connectionId}/remove`);
        } catch (error3) {
          // If all fail, return the original error
          throw error;
        }
      }
    }
  }

  // Activity Invitations - TEMPORARY: Accept both UUIDs and IDs during migration
  async sendActivityInvitation(activityUuid: string, invitedParentUuid: string, childUuid?: string, message?: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/invite`, { 
      invited_parent_uuid: invitedParentUuid,
      child_uuid: childUuid,
      message 
    });
  }

  async getActivityInvitations(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/activity-invitations');
  }

  async getPendingInvitationsForCalendar(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    const batchResponse = await this.getBatchInvitations(startDate, endDate);
    if (!batchResponse.success) {
      return batchResponse as any;
    }

    // Filter to only return PENDING status invitations (not accepted/rejected)
    const pendingOnly = (batchResponse.data?.pending_invitations || []).filter(
      (inv: any) => inv.status === 'pending'
    );

    // Normalize invitation UUIDs: uuid should be activity UUID, invitation_uuid should be invitation UUID
    const normalized = pendingOnly.map((inv: any) => ({
      ...inv,
      invitation_uuid: inv.uuid,  // Preserve invitation UUID from ai.uuid BEFORE overwriting
      invitationUuid: inv.uuid,   // CamelCase version of invitation UUID
      uuid: inv.activity_uuid || inv.uuid,  // Use activity UUID as primary identifier
      activity_uuid: inv.activity_uuid || inv.uuid  // Keep activity_uuid for reference
    }));

    return {
      success: true,
      data: normalized
    };
  }

  async getDeclinedInvitationsForCalendar(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    const batchResponse = await this.getBatchInvitations(startDate, endDate);
    if (!batchResponse.success) {
      return batchResponse as any;
    }

    // Filter rejected invitations from BOTH calendar and pending invitations
    // (Pitchero activities without child_id come from pending_invitations)
    const rejectedFromCalendar = batchResponse.data?.calendar_invitations?.filter(
      invitation => invitation.status === 'rejected' || invitation.status === 'declined'
    ) || [];

    const rejectedFromPending = batchResponse.data?.pending_invitations?.filter(
      invitation => invitation.status === 'rejected' || invitation.status === 'declined'
    ) || [];

    // Combine both arrays
    const rejectedInvitations = [...rejectedFromCalendar, ...rejectedFromPending];

    // Normalize invitation UUIDs: uuid should be activity UUID, invitation_uuid should be invitation UUID
    const normalized = rejectedInvitations.map((inv: any) => ({
      ...inv,
      invitation_uuid: inv.uuid,  // Preserve invitation UUID from ai.uuid BEFORE overwriting
      invitationUuid: inv.uuid,   // CamelCase version of invitation UUID
      uuid: inv.activity_uuid || inv.uuid,  // Use activity UUID as primary identifier
      activity_uuid: inv.activity_uuid || inv.uuid  // Keep activity_uuid for reference
    }));

    return {
      success: true,
      data: normalized
    };
  }

  async getAllInvitationsForCalendar(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    const batchResponse = await this.getBatchInvitations(startDate, endDate);
    if (!batchResponse.success) {
      return batchResponse as any;
    }

    // Return ALL invitations regardless of status (pending, accepted, rejected)
    // This is from the pending_invitations array which despite the name contains all statuses
    const allInvitations = batchResponse.data?.pending_invitations || [];

    // Normalize invitation UUIDs: uuid should be activity UUID, invitation_uuid should be invitation UUID
    const normalized = allInvitations.map((inv: any) => ({
      ...inv,
      invitation_uuid: inv.uuid,  // Preserve invitation UUID from ai.uuid BEFORE overwriting
      invitationUuid: inv.uuid,   // CamelCase version of invitation UUID
      uuid: inv.activity_uuid || inv.uuid,  // Use activity UUID as primary identifier
      activity_uuid: inv.activity_uuid || inv.uuid  // Keep activity_uuid for reference
    }));

    return {
      success: true,
      data: normalized
    };
  }

  async respondToActivityInvitation(invitationUuid: string, action: 'accept' | 'reject', responsibleParentUuid?: string): Promise<ApiResponse<any>> {
    const body: any = { action };
    if (responsibleParentUuid) {
      body.responsible_parent_uuid = responsibleParentUuid;
    }
    const response = await this.request('post', `/api/activity-invitations/${invitationUuid}/respond`, body);
    // Clear cache after responding to ensure fresh data on next load
    if (response.success) {
      this.clearAllCaches();
    }
    return response;
  }

  async snoozeNotification(notificationId: string, reminderPeriod: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/notifications/${encodeURIComponent(notificationId)}/snooze`, {
      reminder_period: reminderPeriod
    });
  }

  // Snoozes every outstanding invitation notification for a child server-side.
  // Returns the notification_ids that were actually written, so the caller only
  // hides what really persisted.
  async snoozeAllNotifications(childUuid: string, reminderPeriod: string): Promise<ApiResponse<{
    snoozed_count: number;
    candidate_count: number;
    notifications: { notification_id: string; remind_at: string }[];
  }>> {
    return this.request('post', '/api/notifications/snooze-all', {
      child_uuid: childUuid,
      reminder_period: reminderPeriod
    });
  }

  async markInvitationAsViewed(invitationUuid: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activity-invitations/${invitationUuid}/view`);
  }

  async markStatusChangeAsViewed(invitationUuid: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activity-invitations/${invitationUuid}/mark-status-viewed`);
  }

  async getActivityParticipants(activityUuid: string, childUuid?: string): Promise<ApiResponse<any>> {
    const url = childUuid
      ? `/api/activities/${activityUuid}/participants?childUuid=${childUuid}`
      : `/api/activities/${activityUuid}/participants`;
    return this.request('get', url);
  }

  async getBatchActivityParticipants(activityUuids: string[]): Promise<ApiResponse<any>> {
    return this.request('post', '/api/activities/batch/participants', { activityUuids });
  }

  async createPendingInvitations(activityUuid: string, pendingConnections: string[]): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/pending-invitations`, { 
      pending_connections: pendingConnections 
    });
  }

  // Activity Templates
  async getActivityTemplates(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/activity-templates');
  }

  async getActivityTypes(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/activity-types');
  }

  // Tentative Activities
  async respondToTentativeActivity(activityUuid: string, childUuid: string, response: 'interested' | 'not_interested'): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/tentative-response`, {
      childUuid,
      response
    });
  }

  async getTentativeResponses(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('get', `/api/activities/${activityUuid}/tentative-responses`);
  }

  async confirmTentativeActivity(activityUuid: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activities/${activityUuid}/confirm`);
  }

  async createActivityTemplate(templateData: any): Promise<ApiResponse<any>> {
    return this.request('post', '/api/activity-templates', templateData);
  }

  async updateActivityTemplate(templateUuid: string, templateData: any): Promise<ApiResponse<any>> {
    return this.request('put', `/api/activity-templates/${templateUuid}`, templateData);
  }

  async deleteActivityTemplate(templateUuid: string): Promise<ApiResponse<any>> {
    return this.request('delete', `/api/activity-templates/${templateUuid}`);
  }

  async useActivityTemplate(templateUuid: string): Promise<ApiResponse<any>> {
    return this.request('post', `/api/activity-templates/${templateUuid}/use`);
  }

  // Club usage increment
  async incrementClubUsage(data: {
    website_url: string;
    activity_type: string;
    location?: string;
    child_age?: number;
    activity_start_date?: string;
    activity_uuid?: string;
  }): Promise<ApiResponse<any>> {
    return this.request('post', '/api/clubs/increment-usage', data);
  }

  // Notification dismissal
  async dismissNotification(notificationId: string, type?: string): Promise<ApiResponse<any>> {
    return this.request('post', '/api/notifications/dismiss', { notificationId, type });
  }

  async getDismissedNotifications(): Promise<ApiResponse<string[]>> {
    return this.request('get', '/api/notifications/dismissed');
  }

  async getBellNotifications(): Promise<ApiResponse<any[]>> {
    return this.request('get', '/api/notifications/bell');
  }

  async createWelcomeNotification(): Promise<ApiResponse<any>> {
    return this.request('post', '/api/notifications/create-welcome');
  }

  async getChildNotifications(childUuid: string): Promise<ApiResponse<any[]>> {
    return this.request('get', `/api/notifications/bell?child_uuid=${childUuid}`);
  }

  async markNotificationAsRead(notificationId: string): Promise<ApiResponse<any>> {
    return this.request('post', '/api/notifications/mark-read', { notificationId });
  }

  // Clubs API
  async getClubs(activityType?: string, search?: string, location?: string): Promise<ApiResponse<any[]>> {
    const params = new URLSearchParams();
    if (activityType) {
      params.append('activity_type', activityType);
    }
    if (search?.trim()) {
      params.append('search', search.trim());
    }
    if (location?.trim()) {
      params.append('location', location.trim());
    }

    const queryString = params.toString();
    const url = `/api/clubs${queryString ? `?${queryString}` : ''}`;

    return this.request('get', url);
  }

  async createClub(clubData: {
    name: string;
    website_url: string;
    location: string;
    venue?: string;
    activity_type: string;
    cost?: string;
    max_participants?: string;
    contact_phone?: string;
    contact_email?: string;
    is_private?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request('post', '/api/clubs', clubData);
  }

  async updateClub(clubId: number, clubData: {
    name: string;
    website_url: string;
    location: string;
    venue?: string;
    activity_type: string;
    cost?: string;
    max_participants?: string;
    contact_phone?: string;
    contact_email?: string;
    is_private?: boolean;
  }): Promise<ApiResponse<any>> {
    return this.request('put', `/api/clubs/${clubId}`, clubData);
  }

  async updateClubKitItems(clubUuid: string, kitItems: string[]): Promise<ApiResponse<any>> {
    return this.request('patch', `/api/clubs/${clubUuid}/kit-items`, { kit_items: kitItems });
  }

  async updateClubKitItemsByName(clubName: string, kitItems: string[]): Promise<ApiResponse<any>> {
    return this.request('patch', `/api/clubs/by-name/${encodeURIComponent(clubName)}/kit-items`, { kit_items: kitItems });
  }

  // ==================== KIT TRACKING ====================

  async getKitTracking(activityUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/activities/${activityUuid}/kit-tracking`);
  }

  async updateKitTracking(activityUuid: string, childUuid: string, itemName: string, isPacked: boolean): Promise<ApiResponse> {
    return this.request('patch', `/api/activities/${activityUuid}/kit-tracking`, {
      itemName,
      isPacked,
      childUuid
    });
  }

  async toggleKitItem(activityUuid: string, itemName: string, isPacked: boolean): Promise<ApiResponse> {
    return this.request('post', `/api/activities/${activityUuid}/kit-tracking/toggle`, {
      itemName,
      isPacked
    });
  }

  async addCustomKitItem(activityUuid: string, itemName: string, applyToSeries: boolean): Promise<ApiResponse> {
    return this.request('post', `/api/activities/${activityUuid}/kit-tracking/add-custom`, {
      itemName,
      applyToSeries
    });
  }

  async removeCustomKitItem(activityUuid: string, itemName: string): Promise<ApiResponse> {
    return this.request('delete', `/api/activities/${activityUuid}/kit-tracking/${encodeURIComponent(itemName)}`);
  }

  // ==================== PITCHERO INTEGRATION ====================

  async savePitcheroCredentials(parentUuid: string, credentials: { username: string; password: string }): Promise<ApiResponse> {
    return this.request('post', `/api/parents/${parentUuid}/pitchero/credentials`, credentials);
  }

  async getPitcheroStatus(parentUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/pitchero/status`);
  }

  async triggerPitcheroSync(parentUuid: string, matchOnly: boolean = false): Promise<ApiResponse> {
    const query = matchOnly ? '?matchOnly=true' : '';
    return this.request('post', `/api/parents/${parentUuid}/pitchero/sync${query}`);
  }

  async importPitcheroActivities(parentUuid: string, excludedClubs?: { [mappingId: number]: string[] }, excludedChildMappingIds?: number[], selectedTeams?: { [mappingId: number]: string[] }): Promise<ApiResponse> {
    return this.request('post', `/api/parents/${parentUuid}/pitchero/import-activities`, { excludedClubs, excludedChildMappingIds, selectedTeams });
  }

  async getPitcheroSyncHistory(parentUuid: string, limit: number = 10): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/pitchero/sync-history?limit=${limit}`);
  }

  async updatePitcheroSettings(parentUuid: string, settings: { sync_enabled?: boolean; disconnect?: boolean }): Promise<ApiResponse> {
    return this.request('patch', `/api/parents/${parentUuid}/pitchero/settings`, settings);
  }

  async savePitcheroManualMatches(parentUuid: string, matches: Array<{ pitchero_member_id: string; child_uuid: string; pitchero_child_name: string }>): Promise<ApiResponse> {
    return this.request('post', `/api/parents/${parentUuid}/pitchero/manual-match`, { matches });
  }

  async getPitcheroMappings(parentUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/pitchero/mappings`);
  }

  async updatePitcheroMapping(parentUuid: string, mappingId: number, childUuid: string): Promise<ApiResponse> {
    return this.request('put', `/api/parents/${parentUuid}/pitchero/mappings/${mappingId}`, { child_uuid: childUuid });
  }

  // School Portal disconnect
  async disconnectSchoolPortal(childUuid: string): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/disconnect', { childUuid });
  }

  // SOCS Integration Methods

  /** Open prompts telling the parent their SOCS setup needs amending. */
  async socsReviewPrompts(): Promise<ApiResponse> {
    return this.request('get', '/api/socs/review-prompts');
  }

  /** Close a prompt - by child (clears all of theirs) or a single prompt. */
  async socsAcknowledgeReviewPrompt(params: { childUuid?: string; promptUuid?: string }): Promise<ApiResponse> {
    return this.request('post', '/api/socs/review-prompts/acknowledge', params);
  }

  async socsSearchSchoolSite(schoolName: string): Promise<ApiResponse> {
    return this.request('post', '/api/socs/search-school-site', { schoolName });
  }

  async socsScrapeSchoolSports(schoolName: string): Promise<ApiResponse> {
    return this.request('post', '/api/socs/scrape-sports', { schoolName });
  }

  async socsScrapeTeams(schoolName: string, sportSid: string, sportUrl: string): Promise<ApiResponse> {
    return this.request('post', '/api/socs/scrape-teams', { schoolName, sportSid, sportUrl });
  }

  async socsScrapeFixtures(schoolName: string, teamTid: string, teamUrl: string, childUuid: string): Promise<ApiResponse> {
    return this.request('post', '/api/socs/scrape-fixtures', { schoolName, teamTid, teamUrl, childUuid });
  }

  async socsScrapeFixturesForChild(
    schoolName: string,
    childUuid: string,
    teams: Array<{ teamTid: string; teamUrl: string }>
  ): Promise<ApiResponse> {
    return this.request('post', '/api/socs/scrape-fixtures-for-child', { schoolName, childUuid, teams });
  }

  async socsPreviewFixtures(schoolName: string, teamTid: string, teamUrl: string, childUuid: string): Promise<ApiResponse> {
    return this.request('post', '/api/socs/preview-fixtures', { schoolName, teamTid, teamUrl, childUuid });
  }

  async socsSetupChild(
    childUuid: string,
    schoolName: string,
    schoolUrl: string,
    teamUuids: string[],
    selectedAgeGroup?: string,
    selectedGenders?: string[],
    selectedLevels?: string[]
  ): Promise<ApiResponse> {
    return this.request('post', '/api/socs/setup-child', {
      childUuid,
      schoolName,
      schoolUrl,
      teamUuids,
      selectedAgeGroup,
      selectedGenders,
      selectedLevels
    });
  }

  async socsGetChildMappings(childUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/socs/child-mappings/${childUuid}`);
  }

  async socsSyncAllFixtures(): Promise<ApiResponse> {
    return this.request('post', '/api/socs/sync-all-fixtures', {});
  }

  async getSOCSStatus(parentUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/socs/status`);
  }

  async socsScrapeAllTeamsByAge(schoolName: string, manualUrl?: string): Promise<ApiResponse> {
    return this.request('post', '/api/socs/scrape-all-teams-by-age', { schoolName, manualUrl });
  }

  async socsFilterTeams(schoolName: string, ageGroup: string, genders: string[], levels: string[]): Promise<ApiResponse> {
    return this.request('post', '/api/socs/filter-teams', { schoolName, ageGroup, genders, levels });
  }

  // ==========================================
  // SCHOOL PORTAL (My School Portal) INTEGRATION
  // ==========================================

  /**
   * Search for My School Portal sites by school name
   */
  async schoolPortalSearchSchoolSite(schoolName: string): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/search-school-site', { schoolName });
  }

  /**
   * Get existing School Portal mappings for a child
   */
  async schoolPortalGetChildMappings(childUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/schoolportal/child/${childUuid}/mappings`);
  }

  /**
   * Sync School Portal calendar for multiple children
   */
  async schoolPortalSync(
    schoolUrl: string,
    email: string,
    password: string,
    childUuids: string[],
    childYearGroups: { [childUuid: string]: string },
    excludeSportFixtures: boolean
  ): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/sync', {
      schoolUrl,
      email,
      password,
      childUuids,
      childYearGroups,
      excludeSportFixtures
    });
  }

  /**
   * Get School Portal integration status for a parent
   */
  async getSchoolPortalStatus(parentUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/schoolportal/status`);
  }

  /**
   * Fetch events from School Portal (scrapes and caches)
   */
  async schoolPortalFetchEvents(
    schoolUrl: string,
    email: string,
    password: string,
    fromDate?: string,
    toDate?: string
  ): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/fetch-events', {
      schoolUrl,
      email,
      password,
      fromDate,
      toDate
    });
  }

  /**
   * Get cached School Portal events with filtering
   */
  async schoolPortalGetCachedEvents(
    siteId: number,
    yearGroup?: string,
    excludeSportFixtures?: boolean,
    fromDate?: string,
    toDate?: string
  ): Promise<ApiResponse> {
    const params = new URLSearchParams();
    if (yearGroup) params.append('yearGroup', yearGroup);
    if (excludeSportFixtures !== undefined) params.append('excludeSportFixtures', String(excludeSportFixtures));
    if (fromDate) params.append('fromDate', fromDate);
    if (toDate) params.append('toDate', toDate);

    const queryString = params.toString();
    return this.request('get', `/api/schoolportal/sites/${siteId}/events${queryString ? '?' + queryString : ''}`);
  }

  /**
   * Import selected School Portal events as activities
   */
  async schoolPortalImportEvents(
    siteUuid: string,
    eventUuids: string[],
    childUuid: string,
    childYearGroup?: string,
    schoolUrl?: string,
    email?: string,
    password?: string,
    yearGroupsFilter?: string[],
    excludeSportFixtures?: boolean,
    importClubs?: boolean
  ): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/import-events', {
      siteUuid,
      eventUuids,
      childUuid,
      childYearGroup,
      schoolUrl,
      email,
      password,
      yearGroupsFilter,
      excludeSportFixtures,
      importClubs
    });
  }

  /**
   * Auto-sync School Portal events weekly (called from home page)
   * Checks if sync needed (>7 days), fetches events, auto-imports ECAs
   */
  async schoolPortalAutoSync(force: boolean = false): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/auto-sync', { force });
  }

  /**
   * Sync School Portal booked bundles (clubs & wrap-around care) for one child.
   * Used after creds are saved for children who selected School Portal as their
   * clubs provider in the onboarding questionnaire.
   */
  async schoolPortalSyncClubs(childUuid: string): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/clubs/sync', { childUuid });
  }

  /**
   * Children whose school-portal year group has changed since their events were
   * imported - the portal moved them up a year. The filter is already updated;
   * the calendar still needs reloading to pull the new year's events.
   */
  async schoolPortalYearGroupChanges(): Promise<ApiResponse> {
    return this.request('get', '/api/schoolportal/year-group-changes');
  }

  /** Clear the year-group-change prompt for one child. */
  async schoolPortalAcknowledgeYearGroupChange(childUuid: string): Promise<ApiResponse> {
    return this.request('post', '/api/schoolportal/year-group-changes/acknowledge', { childUuid });
  }

  // ==========================================
  // SPOND INTEGRATION
  // ==========================================

  async getSpondStatus(parentUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/spond/status`);
  }

  async spondLogin(email: string, password: string): Promise<ApiResponse> {
    return this.request('post', '/api/spond/login', { email, password });
  }

  async spondSetup(email: string, password: string, selectedGroups: any[], childMappings: any[]): Promise<ApiResponse> {
    return this.request('post', '/api/spond/setup', { email, password, selectedGroups, childMappings });
  }

  async spondSync(): Promise<ApiResponse> {
    return this.request('post', '/api/spond/sync', {});
  }

  async spondDisconnect(): Promise<ApiResponse> {
    return this.request('post', '/api/spond/disconnect', {});
  }

  // Term Planner Methods
  async createTermPlanner(parentUuid: string, termData: any): Promise<ApiResponse> {
    return this.request('post', `/api/parents/${parentUuid}/term-planner`, termData);
  }

  async getTermPlanners(parentUuid: string, childUuid?: string): Promise<ApiResponse> {
    const query = childUuid ? `?child_uuid=${childUuid}` : '';
    return this.request('get', `/api/parents/${parentUuid}/term-planners${query}`);
  }

  async getTermPlannersForChild(childUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/term-planners/child/${childUuid}`);
  }

  async getTermPlannerBgStatus(termPlannerUuid: string): Promise<ApiResponse<{ completed: boolean; completed_at: string | null }>> {
    return this.request('get', `/api/term-planners/${termPlannerUuid}/bg-status`);
  }

  // Integration cache readiness for a child. `pending` lists the sources the
  // child is set up for whose club data hasn't landed in club_data_cache yet —
  // the term planner step holds until this comes back ready.
  async getChildClubDataStatus(childUuid: string): Promise<ApiResponse<{
    ready: boolean;
    expected: string[];
    pending: string[];
    sources: { source: string; clubs: number; fetched_at: string }[];
  }>> {
    return this.request('get', `/api/children/${childUuid}/club-data-status`);
  }

  async getTermPlanner(parentUuid: string, termPlannerUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/parents/${parentUuid}/term-planners/${termPlannerUuid}`, undefined, { timeout: 60000 });
  }

  async updateTermPlanner(parentUuid: string, termPlannerUuid: string, termData: any): Promise<ApiResponse> {
    return this.request('put', `/api/parents/${parentUuid}/term-planners/${termPlannerUuid}`, termData);
  }

  async deleteTermPlanner(parentUuid: string, termPlannerUuid: string): Promise<ApiResponse> {
    return this.request('delete', `/api/parents/${parentUuid}/term-planners/${termPlannerUuid}`);
  }

  // ==========================================
  // HOLIDAY PAUSES
  // ==========================================

  async getHolidayPauses(childUuid: string): Promise<ApiResponse> {
    return this.request('get', `/api/children/${childUuid}/holiday-pauses`);
  }

  async createHolidayPause(childUuid: string, data: { pause_start?: string; pause_end?: string; label?: string }): Promise<ApiResponse> {
    return this.request('post', `/api/children/${childUuid}/holiday-pauses`, data);
  }

  async deleteHolidayPause(childUuid: string, pauseUuid: string): Promise<ApiResponse> {
    return this.request('delete', `/api/children/${childUuid}/holiday-pauses/${pauseUuid}`);
  }

  // ==========================================
  // SCHOOLSBUDDY INTEGRATION
  // ==========================================

  /**
   * Test SchoolsBuddy authentication
   */
  async testSchoolsBuddyAuth(email: string, password: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/test-auth`,
        { email, password },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('SchoolsBuddy auth test error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to authenticate with SchoolsBuddy'
      };
    }
  }

  /**
   * Save SchoolsBuddy credentials
   */
  async saveSchoolsBuddyCredentials(email: string, password: string, childId: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/save-credentials`,
        { email, password, childId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Save SchoolsBuddy credentials error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save credentials'
      };
    }
  }

  /**
   * Import SchoolsBuddy activities to term planner
   */
  async importSchoolsBuddyActivities(childUuid: string, termPlannerUuid: string, schoolsBuddyChildId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/import`,
        { childUuid, termPlannerUuid, schoolsBuddyChildId },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Import SchoolsBuddy activities error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to import SchoolsBuddy activities'
      };
    }
  }

  /**
   * Get SchoolsBuddy integration status
   */
  async disconnectSchoolsBuddy(): Promise<ApiResponse> {
    return this.request('post', '/api/schoolsbuddy/disconnect', {});
  }

  // ─── Integration Builder ─────────────────────────────────────────────
  // POSTs a redacted iOS WKWebView capture log to Claude (server-side) and
  // gets back an InferredConnectorConfig. See integration-builder.js on the
  // backend for the prompt + auth taxonomy. `captureLog` MUST be redacted
  // by the iOS capture plugin before reaching here.
  async analyzeIntegrationCapture(payload: {
    appName: string;
    captureLog: any[];
    userHints?: Record<string, any>;
  }): Promise<ApiResponse<{ config: any; raw: string | null; validationError: string | null }>> {
    // Backend runs Claude analysis out-of-band (Heroku H12 = 30s, Claude often
    // takes 60-120s). Enqueue, then poll until done or 4 min cap.
    const enqueue = await this.request<{ jobId: string }>('post', '/api/integrations/builder/analyze', payload);
    if (!enqueue.success || !enqueue.data?.jobId) {
      return { success: false, error: enqueue.error || 'Failed to enqueue analyze job' } as any;
    }
    const jobId = enqueue.data.jobId;
    const deadline = Date.now() + 4 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000));
      const poll = await this.request<any>('get', `/api/integrations/builder/analyze-job/${jobId}`, undefined, { timeout: 15000 });
      if (!poll.success) {
        // 404/500 = job died on server. Don't keep polling.
        return { success: false, error: poll.error || 'Analyze job failed' } as any;
      }
      const status = poll.data?.status;
      if (status === 'pending') continue;
      if (status === 'done') {
        return { success: true, data: { config: poll.data.config, raw: poll.data.raw, validationError: poll.data.validationError } };
      }
      return { success: false, error: poll.data?.error || 'Analyze job errored' } as any;
    }
    return { success: false, error: 'Analyze timed out after 4 minutes' } as any;
  }

  async saveBuiltIntegration(config: any): Promise<ApiResponse<{ integrationUuid: string; status: string }>> {
    return this.request('post', '/api/integrations/builder/save', { config });
  }

  async listCommunityIntegrations(): Promise<ApiResponse<{ integrations: any[] }>> {
    return this.request('get', '/api/integrations/builder/list');
  }

  async connectCommunityIntegration(integrationUuid: string, secrets: any): Promise<ApiResponse> {
    return this.request('post', '/api/integrations/builder/connect', { integrationUuid, secrets });
  }

  async testRunCommunityIntegration(integrationUuid: string): Promise<ApiResponse<{ activitiesWritten: number; clubs: number }>> {
    return this.request('post', `/api/integrations/builder/test-run/${integrationUuid}`);
  }

  // ─── v0.9+ Crowd Connectors (community_integrations) ───────────────────
  async crowdSearch(q: string): Promise<ApiResponse<{ integrations: any[] }>> {
    return this.request('get', `/api/integrations/community?scope=discover&q=${encodeURIComponent(q || '')}`);
  }
  async crowdListMine(): Promise<ApiResponse<{ integrations: any[] }>> {
    return this.request('get', '/api/integrations/community');
  }
  async crowdPicker(uuid: string): Promise<ApiResponse<any>> {
    return this.request('get', `/api/integrations/community/${uuid}/picker`);
  }
  async crowdProbe(uuid: string, body: { username: string; password: string }): Promise<ApiResponse<any>> {
    return this.request('post', `/api/integrations/community/${uuid}/probe`, body);
  }
  async crowdEnable(uuid: string, body: { username: string; password: string; childMapping?: Record<string, string> }): Promise<ApiResponse> {
    return this.request('post', `/api/integrations/community/${uuid}/enable`, body);
  }
  async crowdSyncNow(uuid: string): Promise<ApiResponse<{ activitiesWritten: number; clubs: number }>> {
    return this.request('post', `/api/integrations/community/${uuid}/sync-now`, {});
  }
  async crowdRootUrl(uuid: string): Promise<ApiResponse<{ rootUrl: string }>> {
    return this.request('get', `/api/integrations/community/${uuid}/root-url`);
  }

  async getSchoolsBuddyStatus(): Promise<ApiResponse<any>> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/schoolsbuddy/status`,
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Get SchoolsBuddy status error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get SchoolsBuddy status'
      };
    }
  }

  /**
   * Save SchoolsBuddy child matches
   */
  async saveSchoolsBuddyMatches(matches: Array<{
    schoolsBuddyChildId: string;
    schoolsBuddyChildName?: string;
    cluubzChildUuid: string;
    confidenceScore?: number;
  }>): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/save-matches`,
        { matches },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Save SchoolsBuddy matches error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to save SchoolsBuddy matches'
      };
    }
  }

  /**
   * Sync SchoolsBuddy activities
   */
  async syncSchoolsBuddyActivities(): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/sync`,
        {},
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Sync SchoolsBuddy activities error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to sync SchoolsBuddy activities'
      };
    }
  }

  /**
   * Search for SchoolsBuddy school URL by school name
   */
  async searchSchoolsBuddySchool(schoolName: string): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/search-school`,
        { schoolName },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('SchoolsBuddy school search error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to search for school'
      };
    }
  }

  /**
   * Link SchoolsBuddy account with automatic child matching
   */
  async linkSchoolsBuddyAccount(
    email: string,
    password: string,
    schoolName: string,
    schoolUrl: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/link`,
        { email, password, schoolName, schoolUrl },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Link SchoolsBuddy account error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to link SchoolsBuddy account'
      };
    }
  }

  async importSchoolsBuddyToTermPlanner(
    childUuid: string,
    termPlannerUuid: string,
    schoolsBuddyChildId: string,
    schoolStartTime?: string,
    schoolEndTime?: string,
    clubConfigurations?: Record<string, { kit_items: string[]; designated_parent_uuid: string; uniform_items: string[] }>,
    termName?: string,
    startDate?: string,
    endDate?: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/import`,
        { childUuid, termPlannerUuid, schoolsBuddyChildId, schoolStartTime, schoolEndTime, clubConfigurations, termName, startDate, endDate },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Import SchoolsBuddy to term planner error:', error);
      // Preserve the backend's structured response (in particular the
      // `code: 'NO_ACTIVE_TERM_PLANNER'` + `needsTermPlannerCreation` flag
      // returned on HTTP 409) so the caller can route the user into the
      // term planner creation flow instead of silently failing.
      if (error.response?.data) {
        return error.response.data;
      }
      return {
        success: false,
        error: 'Failed to import to term planner'
      };
    }
  }

  // Preview SchoolsBuddy activities without importing (for configuration step)
  async previewSchoolsBuddyActivities(
    childUuid: string,
    schoolsBuddyChildId: string,
    startDate: string,
    endDate: string
  ): Promise<ApiResponse<any>> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/schoolsbuddy/preview`,
        { childUuid, schoolsBuddyChildId, startDate, endDate },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error: any) {
      console.error('Preview SchoolsBuddy activities error:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to preview activities'
      };
    }
  }
  // Notification Preferences
  async getNotificationPreferences(): Promise<ApiResponse<{ coverage_gap: boolean; daily_summary: boolean }>> {
    return this.request('get', '/api/users/notification-preferences');
  }

  async updateNotificationPreferences(prefs: { coverage_gap?: boolean; daily_summary?: boolean }): Promise<ApiResponse<{ coverage_gap: boolean; daily_summary: boolean }>> {
    return this.request('put', '/api/users/notification-preferences', prefs);
  }
}

export default ApiService;